import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Building2 } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Departments = () => {
  // ✅ Get session data from localStorage
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // ✅ ALL HOOKS FIRST (before any conditional returns)
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    departmentid: null,
    companyid: companyId || null,  // ✅ From session
    departmentcode: '', // Auto-generated
    departmentname: '',
    parentdepartmentid: '',
    departmenthead: '',
    isactive: true
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchEmployees();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, departments]);

  const fetchEmployees = async () => {
    try {
      // ✅ Use apiClient
      const response = await apiClient.get('/Employees.php', {
        params: { isactive: 1, companyid: companyId }
      });
      
      if (response.data.success) {
        setEmployees(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      // ✅ Use apiClient with session companyId
      const response = await apiClient.get('/Departments.php', {
        params: { companyid: companyId }
      });
      
      if (response.data.success) {
        setDepartments(response.data.data || []);
      } else {
        alert('Failed to fetch departments: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching departments. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterDepartments = () => {
    if (!searchTerm.trim()) {
      setFilteredDepartments(departments);
    } else {
      const filtered = departments.filter(dept =>
        dept.departmentname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dept.departmentcode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDepartments(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.departmentname.trim()) {
      newErrors.departmentname = 'Department name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      // ✅ Use apiClient
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Departments.php', {
        ...formData,
        companyid: companyId,  // ✅ Use session companyId
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        if (!editMode && data.data && data.data.departmentcode) {
          alert(`Department created successfully!\nDepartment Code: ${data.data.departmentcode}`);
        } else {
          alert(editMode ? 'Department updated successfully!' : 'Department created successfully!');
        }
        setShowModal(false);
        resetForm();
        fetchDepartments();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving department:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving department. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (department) => {
    setFormData({
      departmentid: department.departmentid,
      companyid: department.companyid,
      departmentcode: department.departmentcode,
      departmentname: department.departmentname,
      parentdepartmentid: department.parentdepartmentid || '',
      departmenthead: department.departmenthead || '',
      isactive: department.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) {
      return;
    }
    
    try {
      // ✅ Use apiClient
      const response = await apiClient.delete('/Departments.php', {
        data: { departmentid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Department deleted successfully!');
        fetchDepartments();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting department:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting department. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      departmentid: null,
      companyid: companyId || null,  // ✅ From session
      departmentcode: '',
      departmentname: '',
      parentdepartmentid: '',
      departmenthead: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ✅ Check session context - AFTER all hooks
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Departments Master</h2>
          <p className="subtitle">Manage organizational departments</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Department
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by department name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredDepartments.length} {filteredDepartments.length === 1 ? 'department' : 'departments'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading departments...</div>
      ) : filteredDepartments.length === 0 ? (
        <div className="empty-state">
          <p>No departments found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Department Name</th>
                <th>Parent Department</th>
                <th>Department Head</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepartments.map((department) => (
                <tr key={department.departmentid}>
                  <td className="font-mono">{department.departmentcode}</td>
                  <td className="font-semibold">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Building2 size={16} color="#667eea" />
                      {department.departmentname}
                    </div>
                  </td>
                  <td>{department.parentdepartmentname || '-'}</td>
                  <td>{department.departmentheadname || '-'}</td>
                  <td>
                    <span className={`status-badge ${department.isactive ? 'active' : 'inactive'}`}>
                      {department.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(department.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(department)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(department.departmentid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Department' : 'Add New Department'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {!editMode && (
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>ℹ️</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                    Department code will be auto-generated (e.g., DEPT001, DEPT002, etc.)
                  </span>
                </div>
              )}
              
              {editMode && formData.departmentcode && (
                <div className="form-group">
                  <label>Department Code (Auto-generated)</label>
                  <input
                    type="text"
                    value={formData.departmentcode}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="departmentname">
                  Department Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="departmentname"
                  value={formData.departmentname}
                  onChange={(e) => setFormData({ ...formData, departmentname: e.target.value })}
                  maxLength="100"
                  placeholder="e.g., Human Resources, Sales, IT"
                  className={errors.departmentname ? 'error' : ''}
                />
                {errors.departmentname && (
                  <span className="error-message">{errors.departmentname}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="parentdepartmentid">Parent Department</label>
                <select
                  id="parentdepartmentid"
                  value={formData.parentdepartmentid}
                  onChange={(e) => setFormData({ ...formData, parentdepartmentid: e.target.value })}
                >
                  <option value="">-- None (Top Level) --</option>
                  {departments
                    .filter(dept => dept.departmentid !== formData.departmentid)
                    .map((dept) => (
                    <option key={dept.departmentid} value={dept.departmentid}>
                      {dept.departmentname} ({dept.departmentcode})
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Optional: Select if this is a sub-department
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="departmenthead">Department Head</label>
                <select
                  id="departmenthead"
                  value={formData.departmenthead}
                  onChange={(e) => setFormData({ ...formData, departmenthead: e.target.value })}
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map((emp) => (
                    <option key={emp.employeeid} value={emp.employeeid}>
                      {emp.employeename} ({emp.employeecode})
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Optional: Assign a manager/head for this department
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Departments;